import axios from 'axios'


const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000/api'  // Fallback for dev
// One-time logging guard for subscription processing tracing
let _processSubscriptionLogged = false


const getCurrentUser = async () => {
  try {
    const token = getAuthToken()
    if (!token) {
      console.warn('No auth token found')
      return null
    }

    const response = await axios.get(`${API_BASE_URL}/user`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (response.data.success && response.data.data) {
      // Store the fresh user data in sessionStorage
      const freshUser = response.data.data

      // Normalize hasCompletedRegistration field (handle both snake_case and camelCase)
      if (freshUser.has_completed_registration !== undefined && freshUser.hasCompletedRegistration === undefined) {
        freshUser.hasCompletedRegistration = freshUser.has_completed_registration
      }

      sessionStorage.setItem('userData', JSON.stringify(freshUser))

      // Save role-specific IDs if available
      if (freshUser.vendor_id) {
        sessionStorage.setItem("vendorId", freshUser.vendor_id)
      }
      if (freshUser.freelancer_id) {
        sessionStorage.setItem("freelancerId", freshUser.freelancer_id)
      }

      return freshUser
    }

    console.warn('Invalid user data response:', response.data)
    return null
  } catch (error) {
    console.error('Error getting current user:', error)

    // If API call fails, try to return cached data from sessionStorage
    try {
      const cachedUserData = sessionStorage.getItem('userData')
      if (cachedUserData) {
        const user = JSON.parse(cachedUserData)
        // Normalize hasCompletedRegistration field (handle both snake_case and camelCase)
        if (user.has_completed_registration !== undefined && user.hasCompletedRegistration === undefined) {
          user.hasCompletedRegistration = user.has_completed_registration
        }
        return user
      }
      return null
    } catch (parseError) {
      console.error('Error parsing cached user data:', parseError)
      return null
    }
  }
}

export const getAuthToken = () => {
  try {
    const tokenData = sessionStorage.getItem('authToken')
    if (!tokenData) return null

    // tokenData may be either a JSON string { token, timestamp } or a raw token string.
    try {
      const parsed = JSON.parse(tokenData)
      // If parsed has token and timestamp, enforce expiry logic
      if (parsed && parsed.token) {
        const { token, timestamp } = parsed
        const now = Date.now()
        const twoHours = 2 * 60 * 60 * 1000 // 2 hours in milliseconds

        if (timestamp && now - timestamp > twoHours) {
          // Token expired, remove it
          sessionStorage.removeItem('authToken')
          return null
        }

        return token
      }
    } catch (parseError) {
      // Not JSON – tokenData is probably the raw token string
      return tokenData
    }

    return null
  } catch (error) {
    console.error('Error getting auth token:', error)
    return null
  }
}

// Helper function to parse years in operation to number
const parseYearsInOperation = (yearsString) => {
  if (!yearsString) return 0

  // If it's already a number, return it
  if (typeof yearsString === 'number') return yearsString

  const yearsMap = {
    '0-1': 0,
    '1-3': 1,
    '3-5': 3,
    '5-10': 5,
    '10+': 10,
    '10-15': 10,
    '15+': 15
  }

  return yearsMap[yearsString] || parseInt(yearsString) || 0
}


const vendorService = {

  async login(credentials) {
    try {
      const response = await axios.post(`${API_BASE_URL}/login`, {
        email: credentials.email,
        password: credentials.password
      })

      console.log("FULL API RESPONSE:", response.data)

      if (!response.data.success) {
        return {
          success: false,
          error: response.data.message || "Login failed"
        }
      }

      const userData = response.data.data

      // Build clean user object
      const user = {
        id: userData.user_id,
        role: userData.role,
        name: userData.name,
        email: userData.email,
        status: userData.status,
        vendor_id: userData.vendor_id || null,
        freelancer_id: userData.freelancer_id || null,
        bgv_status: userData.bgv_status || null,
        admin_review: userData.admin_review || null,
        hasCompletedRegistration: userData.has_completed_registration || false
      }

      // Save token and user data
      const tokenData = {
        token: userData.access_token,
        timestamp: Date.now()
      }
      sessionStorage.setItem("authToken", JSON.stringify(tokenData))
      sessionStorage.setItem("userData", JSON.stringify(user))

      // Save user ID and role-specific IDs if available
      sessionStorage.setItem("userId", user.id)
      if (user.vendor_id) {
        sessionStorage.setItem("vendorId", user.vendor_id)
      }
      if (user.freelancer_id) {
        sessionStorage.setItem("freelancerId", user.freelancer_id)
      }

      return {
        success: true,
        data: user,
        token: userData.access_token,
        message: response.data.message
      }

    } catch (error) {
      console.error("Login error:", error)
      throw new Error(
        error.response?.data?.message ||
        error.response?.data?.error ||
        ""
      )
    }
  },



  async sendOtp({ role, name, email, phone, password }) {
    try {
      const response = await axios.post(`${API_BASE_URL}/send-otp`, {
        role,
        name,
        email,
        phone,
        password,
      })
      return response.data
    } catch (error) {
      // Enhanced error handling
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message)
      } else if (error.response?.data?.error) {
        throw new Error(error.response.data.error)
      } else if (error.code === 'NETWORK_ERROR' || error.code === 'ECONNREFUSED') {
        throw new Error('Unable to connect to server. Please check your connection.')
      } else {
        throw new Error('Failed to send OTP. Please try again.')
      }
    }
  },

  // Verify Email OTP
  async verifyEmailOtp({ otps_id, role, name, email, email_otp, phone, password }) {
    try {
      const response = await axios.post(`${API_BASE_URL}/verify-email-otp`, {
        otps_id,
        role,
        name,
        email,
        email_otp,
        phone,
        password,
      })
      return response.data
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message)
      } else if (error.response?.data?.error) {
        throw new Error(error.response.data.error)
      } else if (error.code === 'NETWORK_ERROR' || error.code === 'ECONNREFUSED') {
        throw new Error('Unable to connect to server. Please check your connection.')
      } else {
        throw new Error('Email OTP verification failed. Please try again.')
      }
    }
  },

  // Verify Phone OTP
  async verifyPhoneOtp({ otps_id, role, name, email, phone, phone_otp, password }) {
    try {
      const response = await axios.post(`${API_BASE_URL}/verify-phone-otp`, {
        otps_id,
        role,
        name,
        email,
        phone,
        phone_otp,
        password,
      })
      return response.data
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message)
      } else if (error.response?.data?.error) {
        throw new Error(error.response.data.error)
      } else if (error.code === 'NETWORK_ERROR' || error.code === 'ECONNREFUSED') {
        throw new Error('Unable to connect to server. Please check your connection.')
      } else {
        throw new Error('Phone OTP verification failed. Please try again.')
      }
    }
  },

  // Register user
  async register({ otps_id, role, name, email, phone, password }) {
    try {
      const response = await axios.post(`${API_BASE_URL}/register`, {
        otps_id,
        role,
        name,
        email,
        phone,
        password,
      })

      // Store registration-related session data if available
      if (response.data) {
        const { userId, vendorId, freelancerId, hasCompletedRegistration, token, access_token } = response.data;

        // Extract token from response (could be token, access_token, or in data.token)
        const authToken = token || access_token || response.data.data?.token || response.data.data?.access_token

        // Store token if available
        if (authToken) {
          const tokenData = {
            token: authToken,
            timestamp: Date.now()
          }
          sessionStorage.setItem("authToken", JSON.stringify(tokenData))
        }

        // Store userId if provided
        if (userId) {
          sessionStorage.setItem('userId', JSON.stringify(userId));
        }

        // Store role-specific IDs if provided
        if (vendorId) {
          sessionStorage.setItem('vendorId', JSON.stringify(vendorId));
        }
        if (freelancerId) {
          sessionStorage.setItem('freelancerId', JSON.stringify(freelancerId));
        }

        // Store registration completion status
        if (hasCompletedRegistration !== undefined) {
          sessionStorage.setItem('hasCompletedRegistration', JSON.stringify(hasCompletedRegistration));
        }

        // Add token to response data for auto-login
        if (authToken && !response.data.token && !response.data.access_token) {
          response.data.token = authToken
        }
      }

      return response.data
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message)
      } else if (error.response?.data?.error) {
        throw new Error(error.response.data.error)
      } else if (error.code === 'NETWORK_ERROR' || error.code === 'ECONNREFUSED') {
        throw new Error('Unable to connect to server. Please check your connection.')
      } else {
        throw new Error('Registration failed. Please try again.')
      }
    }
  },



  async forgotPassword(email) {
    try {
      await new Promise(resolve => setTimeout(resolve, 1000))
      return { success: true, message: 'Password reset link sent' }
    } catch (error) {
      throw new Error(error.message || 'Password reset failed')
    }
  },

  async resetPassword(token, password) {
    try {
      await new Promise(resolve => setTimeout(resolve, 1000))
      return { success: true, message: 'Password reset successfully' }
    } catch (error) {
      throw new Error(error.message || 'Password reset failed')
    }
  },

  async logout() {
    try {
      const token = getAuthToken()

      if (token) {
        // Call logout API if token exists
        await axios.post(`${API_BASE_URL}/logout`, {}, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      }
    } catch (error) {
      console.error('Logout API error:', error)
      // Continue with local cleanup even if API fails
    } finally {
      // Always clear all auth and user-related sessionStorage
      sessionStorage.removeItem('authToken')
      sessionStorage.removeItem('userData')
      sessionStorage.removeItem('userId')
      sessionStorage.removeItem('vendorId')
      sessionStorage.removeItem('freelancerId')
      // Removed selectedRole - vendor app only
      sessionStorage.removeItem('vendorRegistrationData')
      sessionStorage.removeItem('uploadedDocuments')
      sessionStorage.removeItem('vendorStatus')
      sessionStorage.removeItem('vendorActivationDate')
      sessionStorage.removeItem('verificationStatuses')
      sessionStorage.removeItem('vendorResources')
      sessionStorage.removeItem('vendorSubscription')
      sessionStorage.removeItem('freelancerSubscription')
      sessionStorage.removeItem('vendorProfile')

      // Clear cached registration data
      const keysToRemove = []
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i)
        if (key && (key.startsWith('vendor_register_') || key.startsWith('vendor_profile_'))) {
          keysToRemove.push(key)
        }
      }
      keysToRemove.forEach(key => sessionStorage.removeItem(key))

      // Clear localStorage registration drafts
      localStorage.removeItem('vendor_registration_step1')
      localStorage.removeItem('vendor_registration_step2')
      localStorage.removeItem('vendor_registration_step3')
      localStorage.removeItem('vendor_registration_step4')
    }
  },

  async saveLegalDetails(data) {
    try {
      const user = await getCurrentUser()
      const userId = user?.id || user?.user_id
      const token = getAuthToken()

      if (!userId) {
        throw new Error('User not authenticated. Please login again.')
      }

      if (!token) {
        throw new Error('Authentication token not found. Please login again.')
      }

      console.log('Saving legal details for user:', userId, data)

      const response = await axios.post(`${API_BASE_URL}/vendor-register`, {
        userId: userId,
        step: 'legal-details',
        data: data,
        timestamp: new Date().toISOString(),
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      console.log('Legal details saved successfully:', response.data)

      // Store vendor registration data in sessionStorage if available
      if (response.data.data) {
        const vendorData = response.data.data;

        // Store the complete vendor registration data
        sessionStorage.setItem('vendorRegistrationData', JSON.stringify(vendorData));

        // Store specific vendor fields if available
        if (vendorData.vendor_id) {
          sessionStorage.setItem('vendorId', JSON.stringify(vendorData.vendor_id));
        }
        if (vendorData.status) {
          sessionStorage.setItem('vendorStatus', JSON.stringify(vendorData.status));
        }
        if (vendorData.hasCompletedRegistration !== undefined) {
          sessionStorage.setItem('hasCompletedRegistration', JSON.stringify(vendorData.hasCompletedRegistration));
        }
      }

      return response.data
    } catch (error) {
      console.log("LEGAL DETAILS ERROR FULL ===>", error)

      if (error.response?.status === 401) {
        // Token expired or invalid
        sessionStorage.removeItem('authToken')
        sessionStorage.removeItem('userData')
        throw new Error('Session expired. Please login again.')
      }

      if (error.response) {
        console.log("RESPONSE DATA ===>", error.response.data)
        console.log("STATUS ===>", error.response.status)
        console.log("HEADERS ===>", error.response.headers)
        throw new Error(error.response.data.message || 'Failed to save legal details')
      } else if (error.request) {
        console.log("NO RESPONSE FROM SERVER. REQUEST:", error.request)
        throw new Error('No response from server. Please check your connection.')
      } else {
        console.log("ERROR SETUP:", error.message)
        throw new Error('Failed to save legal details. Please try again.')
      }
    }
  },

  async saveProfileDetails(data) {
    try {
      const user = await getCurrentUser();
      const userId = user?.id || user?.user_id;
      const token = getAuthToken();

      if (!userId) {
        throw new Error('User not authenticated. Please login again.');
      }

      // CRITICAL: Get vendor_id from multiple sources
      let vendorId = data.vendor_id;

      // If not provided in data, try to get from user object
      if (!vendorId || vendorId === "" || vendorId === "null") {
        vendorId = user?.vendor_id || user?.vendorId || null;
      }

      // If still not found, try sessionStorage
      if (!vendorId || vendorId === "" || vendorId === "null") {
        const storedVendorId = sessionStorage.getItem('vendorId');
        if (storedVendorId) {
          try {
            vendorId = JSON.parse(storedVendorId);
          } catch (e) {
            vendorId = storedVendorId;
          }
        }
      }

      // Convert empty string or invalid values to null
      if (vendorId === "" || vendorId === "null" || vendorId === "undefined") {
        vendorId = null;
      }

      // Convert to number if it's a string number
      if (vendorId && !isNaN(vendorId)) {
        vendorId = parseInt(vendorId, 10);
      }

      console.log('saveProfileDetails: Final vendor_id being sent:', vendorId);
      console.log('saveProfileDetails: User object vendor_id:', user?.vendor_id || user?.vendorId);

      // Prepare JSON payload - handle both camelCase and snake_case formats
      const industryDomains = data.industry_domains || data.industryDomains || [];
      const keyTechnologies = data.key_technologies || data.keyTechnologies || [];
      const yearsInOp = data.years_in_operation !== undefined
        ? data.years_in_operation
        : (data.yearsInOperation ? parseYearsInOperation(data.yearsInOperation) : 0);
      const socialHandles = data.other_social_handles || data.otherSocialHandles || '';

      // Prepare FormData for multipart/form-data with JSON payload
      const formData = new FormData();

      // Create JSON payload with all text fields - ensure completely primitive values
      const payload = {
        vendor_id: vendorId ? String(vendorId) : null,
        user_id: (data.user_id || userId) ? String(data.user_id || userId) : null,
        short_description: String((data.short_description || '').trim() || ''),
        detailed_description: String((data.detailed_description || '').trim() || ''),
        industry_domains: Array.isArray(industryDomains) && industryDomains.length > 0
          ? industryDomains.map(item => String(item))
          : [],
        years_in_operation: Number(yearsInOp) || 0,
        key_technologies: Array.isArray(keyTechnologies) && keyTechnologies.length > 0
          ? keyTechnologies.map(item => String(item))
          : [],
        website_url: (data.website_url || data.websiteUrl) ? String((data.website_url || data.websiteUrl).trim()) : null,
        linkedin_url: (data.linkedin_url || data.linkedinPage) ? String((data.linkedin_url || data.linkedinPage).trim()) : null,
        other_social_handles: (typeof socialHandles === 'string' && socialHandles.trim())
          ? String(socialHandles.trim())
          : null
      };

      // Add JSON payload as form field
      formData.append('payload', JSON.stringify(payload));

      // Attach files with correct field names
      if (data.logo?.file) {
        formData.append('logo', data.logo.file);
      }
      if (data.brand_assets?.file) {
        formData.append('brand_assets', data.brand_assets.file);
      }



      const response = await fetch(`${API_BASE_URL}/vendor-profile`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // 'Content-Type' is set automatically by browser for FormData
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Backend error response:', errorData);
        let errorMessage = 'Failed to save profile details';
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            errorMessage = errorData.detail[0]?.msg || errorMessage;
          } else if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (errorData.detail.message) {
            errorMessage = errorData.detail.message;
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('Profile details saved successfully:', result);

      // Store profile data in sessionStorage if available
      if (result.data) {
        const profileData = result.data;

        // Store the complete vendor profile
        sessionStorage.setItem('vendorProfile', JSON.stringify(profileData));

        // Store specific profile fields if available
        if (profileData.vendor_id) {
          sessionStorage.setItem('vendorId', JSON.stringify(profileData.vendor_id));
        }
        if (profileData.status) {
          sessionStorage.setItem('vendorStatus', JSON.stringify(profileData.status));
        }
      }

      return result;
    } catch (error) {
      console.error('Error saving profile details:', error);
      if (error.message && error.message.includes('auth')) {
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('userData');
        throw new Error('Session expired. Please login again.');
      }
      throw new Error(error.message || 'Failed to save profile details. Please try again.');
    }
  },

  async processSubscriptionPayment(paymentData) {
    try {
      const token = getAuthToken()

      if (!token) {
        throw new Error('User not authenticated. Please login again.')
      }

      // One-time trace log to help debug first invocation only
      if (!_processSubscriptionLogged) {
        console.log('[TRACE] processSubscriptionPayment called with:', paymentData)
        _processSubscriptionLogged = true
      }

      // Use role instead of user_type for consistency with auth context
      let role = paymentData.role || ''; // Default to vendor for backward compatibility

      // If role is not provided, try to get it from current user
      if (!role) {
        const currentUser = await getCurrentUser();
        role = currentUser?.role || 'vendor'; // Default to vendor if not found
      }

      // Fill in missing role-specific IDs from sessionStorage if not provided
      if (role === 'vendor' && !paymentData.vendor_id) {
        const vendorIdStr = sessionStorage.getItem('vendorId');
        paymentData.vendor_id = vendorIdStr ? parseInt(vendorIdStr, 10) : null;
      }
      if (role === 'freelancer' && !paymentData.freelancer_id) {
        const freelancerIdStr = sessionStorage.getItem('freelancerId');
        paymentData.freelancer_id = freelancerIdStr ? parseInt(freelancerIdStr, 10) : null;
      }

      // Use unified role-subscription endpoint
      let endpoint = `${API_BASE_URL}/${role}-subscription`;

      // Create a clean data object with all required fields
      const apiPayload = {
        // Common fields for all user types
        user_id: paymentData.user_id ? parseInt(paymentData.user_id, 10) : null,
        role: role, // Use role instead of user_type
        plan_name: paymentData.plan_name || 'Basic',
        billing_cycle: paymentData.billing_cycle || 'Monthly',
        payment_method: paymentData.payment_method,
        promo_code: paymentData.promo_code,
        amount: paymentData.amount ? parseFloat(paymentData.amount) : 0,
        currency: paymentData.currency || 'INR',

        // Role specific ID fields
        ...(role === 'vendor' && {
          vendor_id: paymentData.vendor_id ? parseInt(paymentData.vendor_id, 10) : null
        }),
        ...(role === 'freelancer' && {
          freelancer_id: paymentData.freelancer_id ? parseInt(paymentData.freelancer_id, 10) : null
        }),

        // Razorpay payment details (if available)
        ...(paymentData.razorpay_payment_id && {
          razorpay_payment_id: paymentData.razorpay_payment_id,
          razorpay_order_id: paymentData.razorpay_order_id,
          razorpay_signature: paymentData.razorpay_signature
        }),

        // Include other payment details for different payment methods
        ...(paymentData.payment_method === 'Card' && {
          cardholder_name: paymentData.cardholder_name,
          card_number: paymentData.card_number
        }),
        ...(paymentData.payment_method === 'UPI' && {
          upi_id: paymentData.upi_id
        }),
        ...(paymentData.payment_method === 'NetBanking' && {
          bank_name: paymentData.bank_name
        }),

        // Additional metadata
        timestamp: new Date().toISOString(),
        status: 'completed'
      }

      // Remove undefined and null fields
      Object.keys(apiPayload).forEach(key => {
        if (apiPayload[key] === undefined || apiPayload[key] === null) {
          delete apiPayload[key]
        }
      })

      // Validate required fields
      if (!apiPayload.user_id || isNaN(apiPayload.user_id)) {
        throw new Error('Valid user ID is required for payment processing')
      }

      if (apiPayload.amount < 0) {
        throw new Error('Valid payment amount is required')
      }

      console.log('Final API payload:', apiPayload)

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(apiPayload)
      })

      if (!response.ok) {
        const errorData = await response.json()
        console.error('Backend error response:', errorData)

        // Provide more specific error messages based on role
        let errorMessage = 'Payment failed'
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            errorMessage = errorData.detail[0]?.msg || errorMessage
          } else if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail
          } else if (errorData.detail.message) {
            errorMessage = errorData.detail.message
          }
        } else if (errorData.message) {
          errorMessage = errorData.message
        }

        throw new Error(errorMessage)
      }

      const result = await response.json()
      console.log('Payment successful:', result)

      // Store subscription data in sessionStorage for future use
      if (result.subscription_id) {
        const subscriptionInfo = {
          subscription_id: result.subscription_id,
          plan_name: paymentData.plan_name,
          billing_cycle: paymentData.billing_cycle,
          role: role, // Use role instead of user_type
          expires_at: result.expires_at,
          status: 'active'
        }

        // Store based on role
        if (role === 'vendor') {
          sessionStorage.setItem('vendorSubscription', JSON.stringify(subscriptionInfo))
        } else if (role === 'freelancer') {
          sessionStorage.setItem('freelancerSubscription', JSON.stringify(subscriptionInfo))
        }
      }

      return result

    } catch (error) {
      console.error('Payment processing error:', error)

      // Enhanced error handling with user-specific messages
      if (error.message.includes('network') || error.message.includes('fetch')) {
        throw new Error('Network error. Please check your internet connection and try again.')
      } else if (error.message.includes('auth') || error.message.includes('token')) {
        throw new Error('Authentication failed. Please log in again.')
      } else if (error.message.includes('subscription') || error.message.includes('plan')) {
        throw new Error('Subscription processing failed. Please try a different plan or contact support.')
      }

      throw error
    }
  },


  // Helper method to get current user
  getCurrentUser,

  // Helper method to check if user is authenticated
  async isAuthenticated() {
    const token = getAuthToken()
    const user = await getCurrentUser()
    return !!(token && user)
  },









  // Get headers for authenticated requests (works for all roles)
  getAuthHeaders() {
    const token = getAuthToken()
    if (!token) return {}

    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  },

  // Document Upload API
  async uploadDocument(documentData) {
    try {
      const user = await getCurrentUser();
      const userId = user?.id || user?.user_id;
      const token = getAuthToken();
      const vendorId = sessionStorage.getItem('vendorId');

      if (!userId) {
        throw new Error('User not authenticated. Please login again.');
      }

      if (!token) {
        throw new Error('Authentication token not found. Please login again.');
      }

      console.log('Uploading document for user:', userId);
      console.log('Document data:', documentData);

      // Create FormData for file upload
      const formData = new FormData();

      // Add file
      if (documentData.file) {
        formData.append('file', documentData.file);
      }

      // Add metadata
      formData.append('document_type', documentData.document_type || '');
      formData.append('document_name', documentData.document_name || '');
      formData.append('document_category', documentData.document_category || 'other');
      formData.append('user_id', userId);
      formData.append('user_role', documentData.user_role || 'vendor');

      // Add optional fields
      if (documentData.description) {
        formData.append('description', documentData.description);
      }

      if (documentData.vendor_id) {
        formData.append('vendor_id', documentData.vendor_id);
      }
      if (documentData.freelancer_id) {
        formData.append('freelancer_id', documentData.freelancer_id);
      }

      const response = await axios.post(
        `${API_BASE_URL}/documents/upload`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          timeout: 30000, // 30 second timeout for large files
        }
      );

      console.log('Document uploaded successfully:', response.data);

      // Store uploaded document data in sessionStorage
      if (response.data.data) {
        const uploadedDoc = response.data.data;

        // Get existing uploaded documents or initialize empty array
        const existingDocs = JSON.parse(sessionStorage.getItem('uploadedDocuments') || '[]');

        // Add the new document to the array
        existingDocs.push(uploadedDoc);

        // Store updated documents array
        sessionStorage.setItem('uploadedDocuments', JSON.stringify(existingDocs));
      }

      return response.data;

    } catch (error) {
      console.error('Error uploading document:', error);

      // Detailed error handling
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
        console.error('Response headers:', error.response.headers);

        if (error.response.status === 401) {
          sessionStorage.removeItem('authToken');
          sessionStorage.removeItem('userData');
          throw new Error('Session expired. Please login again.');
        } else if (error.response.status === 413) {
          throw new Error('File too large. Please upload a smaller file.');
        } else if (error.response.status === 415) {
          throw new Error('Unsupported file type. Please upload a valid document.');
        }

        throw new Error(error.response.data?.message || error.response.data?.detail || 'Failed to upload document');
      } else if (error.request) {
        console.error('No response received:', error.request);
        throw new Error('Cannot connect to server. Please check your internet connection.');
      } else {
        console.error('Setup error:', error.message);
        throw new Error('Failed to upload document. Please try again.');
      }
    }

  },



  // Debug method to check vendor profile response
  async debugVendorProfile(userId) {
    try {
      const token = getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await axios.get(`${API_BASE_URL}/vendor/profile/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      console.log('Full API Response:', response.data)
      console.log('Response data.data:', response.data.data)
      console.log('Subscription in response:', response.data.data?.vendor_subscription_details || response.data.data?.subscription)

      return response.data
    } catch (error) {
      console.error('Error in debug method:', error)
      throw error
    }
  },






  async getVendorDashboard(userId) {
    try {
      const token = getAuthToken()
      if (!token) {
        throw new Error('Authentication required')
      }

      const response = await axios.get(
        `${API_BASE_URL}/vendor/dashboard/${userId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch vendor dashboard data')
      }

      // Store vendor dashboard data in sessionStorage
      if (response.data.data) {
        const dashboardData = response.data.data

        // Store vendor status if available
        if (dashboardData.status) {
          sessionStorage.setItem('vendorStatus', dashboardData.status)
        }

        // Store vendor activation date if available
        if (dashboardData.activation_date) {
          sessionStorage.setItem('vendorActivationDate', dashboardData.activation_date)
        }

        // Store document statuses if available
        if (dashboardData.all_documents_urls) {
          sessionStorage.setItem('uploadedDocuments', JSON.stringify(dashboardData.all_documents_urls))
        }

        // Update verification statuses based on dashboard data
        const verificationStatuses = {
          documents: dashboardData.status === 'active' ? 'completed' : (dashboardData.documents_sent && dashboardData.documents_sent.length > 0 ? 'completed' : 'pending'),
          bgv: dashboardData.Vendor_BGV === 'completed' ? 'completed' : dashboardData.Vendor_BGV || 'pending',
          adminReview: dashboardData.Venodr_Admin_review === 'approved' ? 'completed' : dashboardData.Venodr_Admin_review || 'pending',
          approval: dashboardData.status === 'active' ? 'completed' : 'pending'
        }
        sessionStorage.setItem('verificationStatuses', JSON.stringify(verificationStatuses))
      }

      return response.data
    } catch (error) {
      console.error('Vendor dashboard fetch error:', error)
      throw error
    }
  },

  async addPersonalInfo(personalData) {
    try {
      const token = getAuthToken()
      if (!token) {
        throw new Error('Authentication required')
      }

      console.log('Sending personal info:', personalData)

      const response = await axios.post(
        `${API_BASE_URL}/vendor/resource/add-personal-info`,
        personalData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to save personal information')
      }

      console.log('Personal info saved successfully:', response.data)
      return response.data
    } catch (error) {
      console.error('Add personal info error:', error)

      // Extract detailed error message from API response
      if (error.response) {
        const errorData = error.response.data
        console.error('API Error Response:', errorData)

        // Handle validation errors
        if (error.response.status === 422 && errorData.detail) {
          const validationErrors = Array.isArray(errorData.detail)
            ? errorData.detail.map(err => `${err.loc?.join('.')}: ${err.msg}`).join(', ')
            : JSON.stringify(errorData.detail)

          throw new Error(`Validation Error: ${validationErrors}`)
        }

        // Handle other API errors
        if (errorData.message || errorData.detail) {
          throw new Error(errorData.message || JSON.stringify(errorData.detail))
        }
      }

      throw error
    }
  },

  async addProfessionalInfo(professionalData) {
    try {
      const token = getAuthToken()
      if (!token) {
        throw new Error('Authentication required')
      }

      console.log('Sending professional info:', professionalData)

      const response = await axios.post(
        `${API_BASE_URL}/vendor/resource/add-professional-info`,
        professionalData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to save professional information')
      }

      console.log('Professional info saved successfully:', response.data)
      return response.data
    } catch (error) {
      console.error('Add professional info error:', error)

      // Extract detailed error message from API response
      if (error.response) {
        const errorData = error.response.data
        console.error('API Error Response:', errorData)

        // Handle validation errors
        if (error.response.status === 422 && errorData.detail) {
          const validationErrors = Array.isArray(errorData.detail)
            ? errorData.detail.map(err => `${err.loc?.join('.')}: ${err.msg}`).join(', ')
            : JSON.stringify(errorData.detail)

          throw new Error(`Validation Error: ${validationErrors}`)
        }

        // Handle other API errors
        if (errorData.message || errorData.detail) {
          throw new Error(errorData.message || JSON.stringify(errorData.detail))
        }
      }

      throw error
    }
  },

  async getPersonalInfo(resourceId) {
    try {
      const token = getAuthToken()
      if (!token) {
        throw new Error('Authentication required')
      }

      console.log('Fetching personal info for resource:', resourceId)

      const response = await axios.get(
        `${API_BASE_URL}/vendor/resource/${resourceId}/personal-info`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch personal information')
      }

      console.log('Personal info fetched successfully:', response.data)
      return response.data
    } catch (error) {
      console.error('Get personal info error:', error)

      // Extract detailed error message from API response
      if (error.response) {
        const errorData = error.response.data
        console.error('API Error Response:', errorData)

        // Handle not found errors gracefully
        if (error.response.status === 404) {
          return { success: false, message: 'Personal information not found' }
        }

        // Handle other API errors
        if (errorData.message || errorData.detail) {
          throw new Error(errorData.message || JSON.stringify(errorData.detail))
        }
      }

      throw error
    }
  },

  async getProfessionalInfo(resourceId) {
    try {
      const token = getAuthToken()
      if (!token) {
        throw new Error('Authentication required')
      }

      console.log('Fetching professional info for resource:', resourceId)

      const response = await axios.get(
        `${API_BASE_URL}/vendor/resource/${resourceId}/professional-info`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch professional information')
      }

      console.log('Professional info fetched successfully:', response.data)
      return response.data
    } catch (error) {
      console.error('Get professional info error:', error)

      // Extract detailed error message from API response
      if (error.response) {
        const errorData = error.response.data
        console.error('API Error Response:', errorData)

        // Handle not found errors gracefully
        if (error.response.status === 404) {
          return { success: false, message: 'Professional information not found' }
        }

        // Handle other API errors
        if (errorData.message || errorData.detail) {
          throw new Error(errorData.message || JSON.stringify(errorData.detail))
        }
      }

      throw error
    }
  },

  async getEducationInfo(resourceId) {
    try {
      const token = getAuthToken()
      if (!token) {
        throw new Error('Authentication required')
      }

      console.log('Fetching education info for resource:', resourceId)

      const response = await axios.get(
        `${API_BASE_URL}/vendor/resource/${resourceId}/educations`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch education information')
      }

      console.log('Education info fetched successfully:', response.data)
      return response.data
    } catch (error) {
      console.error('Get education info error:', error)

      // Extract detailed error message from API response
      if (error.response) {
        const errorData = error.response.data
        console.error('API Error Response:', errorData)

        // Handle not found errors gracefully
        if (error.response.status === 404) {
          return { success: false, message: 'Education information not found' }
        }

        // Handle other API errors
        if (errorData.message || errorData.detail) {
          throw new Error(errorData.message || JSON.stringify(errorData.detail))
        }
      }

      throw error
    }
  },

  async getExperienceInfo(resourceId) {
    try {
      const token = getAuthToken()
      if (!token) {
        throw new Error('Authentication required')
      }

      console.log('Fetching experience info for resource:', resourceId)

      const response = await axios.get(
        `${API_BASE_URL}/vendor/resource/${resourceId}/experiences`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch experience information')
      }

      console.log('Experience info fetched successfully:', response.data)
      return response.data
    } catch (error) {
      console.error('Get experience info error:', error)

      // Extract detailed error message from API response
      if (error.response) {
        const errorData = error.response.data
        console.error('API Error Response:', errorData)

        // Handle not found errors gracefully
        if (error.response.status === 404) {
          return { success: false, message: 'Experience information not found' }
        }

        // Handle other API errors
        if (errorData.message || errorData.detail) {
          throw new Error(errorData.message || JSON.stringify(errorData.detail))
        }
      }

      throw error
    }
  },

  async getCertificationInfo(resourceId) {
    try {
      const token = getAuthToken()
      if (!token) {
        throw new Error('Authentication required')
      }

      console.log('Fetching certification info for resource:', resourceId)

      const response = await axios.get(
        `${API_BASE_URL}/vendor/resource/${resourceId}/certifications`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch certification information')
      }

      console.log('Certification info fetched successfully:', response.data)
      return response.data
    } catch (error) {
      console.error('Get certification info error:', error)

      // Extract detailed error message from API response
      if (error.response) {
        const errorData = error.response.data
        console.error('API Error Response:', errorData)

        // Handle not found errors gracefully
        if (error.response.status === 404) {
          return { success: false, message: 'Certification information not found' }
        }

        // Handle other API errors
        if (errorData.message || errorData.detail) {
          throw new Error(errorData.message || JSON.stringify(errorData.detail))
        }
      }

      throw error
    }
  },
  async getBaseDocuments(resourceId) {
    try {
      const token = getAuthToken()
      if (!token) {
        throw new Error('Authentication required')
      }

      console.log('Fetching base documents for resource:', resourceId)

      const response = await axios.get(
        `${API_BASE_URL}/vendor/resource/${resourceId}/base-documents`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch base documents')
      }

      console.log('Base documents fetched successfully:', response.data)
      return response.data
    } catch (error) {
      console.error('Get base documents error:', error)

      if (error.response) {
        const errorData = error.response.data
        if (error.response.status === 404) {
          return { success: false, message: 'Base documents not found' }
        }
        if (errorData.message || errorData.detail) {
          throw new Error(errorData.message || JSON.stringify(errorData.detail))
        }
      }
      throw error
    }
  },

  async addEducationInfo(educationData) {
    try {
      const token = getAuthToken()
      if (!token) {
        throw new Error('Authentication required')
      }

      console.log('Sending education info:', educationData)

      // Create FormData for multipart/form-data submission
      const formData = new FormData()

      // Add resource_id
      formData.append('resource_id', educationData.resource_id)

      // Add education_data as JSON string
      formData.append('education_data', JSON.stringify(educationData.education_data))

      // Add certificate files if present
      if (educationData.certificates && educationData.certificates.length > 0) {
        educationData.certificates.forEach((file) => {
          formData.append('certificates', file)
        })
      }

      // Add file_indexes if present
      if (educationData.file_indexes && educationData.file_indexes.length > 0) {
        formData.append('file_indexes', JSON.stringify(educationData.file_indexes))
      }

      const response = await axios.post(
        `${API_BASE_URL}/vendor/resource/add-educations`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            // Content-Type is set automatically by axios for FormData
          }
        }
      )

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to save education information')
      }

      console.log('Education info saved successfully:', response.data)
      return response.data
    } catch (error) {
      console.error('Add education info error:', error)

      // Extract detailed error message from API response
      if (error.response) {
        const errorData = error.response.data
        console.error('API Error Response:', errorData)

        // Handle validation errors
        if (error.response.status === 422 && errorData.detail) {
          const validationErrors = Array.isArray(errorData.detail)
            ? errorData.detail.map(err => `${err.loc?.join('.')}: ${err.msg}`).join(', ')
            : JSON.stringify(errorData.detail)

          throw new Error(`Validation Error: ${validationErrors}`)
        }

        // Handle other API errors
        if (errorData.message || errorData.detail) {
          throw new Error(errorData.message || JSON.stringify(errorData.detail))
        }
      }

      throw error
    }
  },

  async addExperienceInfo(experienceData) {
    try {
      const token = getAuthToken()
      if (!token) {
        throw new Error('Authentication required')
      }

      console.log('Sending experience info:', experienceData)

      // Create FormData for multipart/form-data submission
      const formData = new FormData()

      // Add resource_id
      formData.append('resource_id', experienceData.resource_id)

      // Add experience_data as JSON string
      formData.append('experience_data', JSON.stringify(experienceData.experience_data))

      // Add certificate files (offer letters, relieving letters, payslips) if present
      if (experienceData.certificates && experienceData.certificates.length > 0) {
        experienceData.certificates.forEach((file) => {
          formData.append('certificates', file)
        })
      }

      // Add file_indexes mapping files to experience entries and document slots
      if (experienceData.file_indexes && experienceData.file_indexes.length > 0) {
        formData.append('file_indexes', JSON.stringify(experienceData.file_indexes))
      }

      const response = await axios.post(
        `${API_BASE_URL}/vendor/resource/add-experience`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            // Content-Type is set automatically by axios for FormData
          }
        }
      )

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to save experience information')
      }

      console.log('Experience info saved successfully:', response.data)
      return response.data
    } catch (error) {
      console.error('Add experience info error:', error)

      // Extract detailed error message from API response
      if (error.response) {
        const errorData = error.response.data
        console.error('API Error Response:', errorData)

        // Handle validation errors
        if (error.response.status === 422 && errorData.detail) {
          const validationErrors = Array.isArray(errorData.detail)
            ? errorData.detail.map(err => `${err.loc?.join('.')}: ${err.msg}`).join(', ')
            : JSON.stringify(errorData.detail)

          throw new Error(`Validation Error: ${validationErrors}`)
        }

        // Handle other API errors
        if (errorData.message || errorData.detail) {
          throw new Error(errorData.message || JSON.stringify(errorData.detail))
        }
      }

      throw error
    }
  },

  async addCertificationInfo(certificationData) {
    try {
      const token = getAuthToken()
      if (!token) {
        throw new Error('Authentication required')
      }

      console.log('Sending certification info:', certificationData)

      // Create FormData for multipart/form-data submission
      const formData = new FormData()

      // Add resource_id
      formData.append('resource_id', certificationData.resource_id)

      // Add certification_data as JSON string
      formData.append('certification_data', JSON.stringify(certificationData.certification_data))

      // Add certificate files if present
      if (certificationData.certificates && certificationData.certificates.length > 0) {
        certificationData.certificates.forEach((file) => {
          formData.append('certificates', file)
        })
      }

      // Add file_indexes mapping files to certification entries
      if (certificationData.file_indexes && certificationData.file_indexes.length > 0) {
        formData.append('file_indexes', JSON.stringify(certificationData.file_indexes))
      }

      const response = await axios.post(
        `${API_BASE_URL}/vendor/resource/add-certifications`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            // Content-Type is set automatically by axios for FormData
          }
        }
      )

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to save certification information')
      }

      console.log('Certification info saved successfully:', response.data)
      return response.data
    } catch (error) {
      console.error('Add certification info error:', error)

      // Extract detailed error message from API response
      if (error.response) {
        const errorData = error.response.data
        console.error('API Error Response:', errorData)

        // Handle validation errors
        if (error.response.status === 422 && errorData.detail) {
          const validationErrors = Array.isArray(errorData.detail)
            ? errorData.detail.map(err => `${err.loc?.join('.')}: ${err.msg}`).join(', ')
            : JSON.stringify(errorData.detail)

          throw new Error(`Validation Error: ${validationErrors}`)
        }

        // Handle other API errors
        if (errorData.message || errorData.detail) {
          throw new Error(errorData.message || JSON.stringify(errorData.detail))
        }
      }

      throw error
    }
  },

  async addVendorResource(resourceData) {
    try {
      const token = getAuthToken()
      if (!token) {
        throw new Error('Authentication required')
      }

      // Send payload as FormData with payload_json field (as stringified JSON string)
      const formData = new FormData()
      formData.append('payload_json', JSON.stringify(resourceData))

      console.log('Sending FormData with payload_json:', JSON.stringify(resourceData))

      const response = await axios.post(
        `${API_BASE_URL}/vendor/resource/add`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`
            // Don't set Content-Type - let axios set it automatically for FormData
          }
        }
      )

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to add resource')
      }

      return response.data
    } catch (error) {
      console.error('Add vendor resource error:', error)

      // Extract detailed error message from API response
      if (error.response) {
        const errorData = error.response.data
        console.error('API Error Response:', errorData)

        // Handle validation errors
        if (error.response.status === 422 && errorData.detail) {
          const validationErrors = Array.isArray(errorData.detail)
            ? errorData.detail.map(err => `${err.loc?.join('.')}: ${err.msg}`).join(', ')
            : JSON.stringify(errorData.detail)

          throw new Error(`Validation Error: ${validationErrors}`)
        }

        // Handle other API errors
        if (errorData.message || errorData.detail) {
          throw new Error(errorData.message || JSON.stringify(errorData.detail))
        }
      }

      throw error
    }
  },

  async getVendorBenchResources(vendorId) {
    try {
      const token = getAuthToken()
      if (!token) {
        throw new Error('Authentication required')
      }

      const response = await axios.get(
        `${API_BASE_URL}/vendor/bench-resources`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          // params: {
          //   vendor_id: vendorId
          // }
        }
      )

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch bench resources')
      }

      // Store vendor resources in sessionStorage
      if (response.data.data) {
        sessionStorage.setItem('vendorResources', JSON.stringify(response.data.data))
      }

      return response.data
    } catch (error) {
      console.error('Get vendor bench resources error:', error)
      throw error
    }
  },

  // Fetch a single bench resource by ID: /bench-resource/{resource_id}
  async getBenchResource(resourceId) {
    try {
      const token = getAuthToken()
      if (!token) {
        throw new Error('Authentication required')
      }

      const endpoint = `${API_BASE_URL}/bench-resource/${resourceId}`
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const err = await response.json().catch(() => ({}))
        const msg = err.message || 'Failed to fetch bench resource'
        throw new Error(msg)
      }

      const data = await response.json()

      // Cache the fetched bench resource inside vendorResources (merge/replace by id)
      if (data && data.data) {
        try {
          const existing = JSON.parse(sessionStorage.getItem('vendorResources') || '[]')
          const idx = existing.findIndex(r => r.id === data.data.id)
          if (idx >= 0) {
            existing[idx] = data.data
          } else {
            existing.push(data.data)
          }
          sessionStorage.setItem('vendorResources', JSON.stringify(existing))
        } catch (e) {
          // If caching fails, don't block the main response
          console.warn('Failed to cache bench resource:', e)
        }
      }

      return data
    } catch (error) {
      console.error('getBenchResource error:', error)
      throw new Error(error.message || 'Failed to fetch bench resource')
    }
  },

  // Delete a bench resource by ID
  async deleteBenchResource(resourceId) {
    try {
      const token = getAuthToken()
      if (!token) {
        throw new Error('Authentication required')
      }

      const endpoint = `${API_BASE_URL}/bench-resource/${resourceId}`
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const err = await response.json().catch(() => ({}))
        const msg = err.message || 'Failed to delete bench resource'
        throw new Error(msg)
      }

      const data = await response.json()

      // Update sessionStorage cache
      try {
        const existing = JSON.parse(sessionStorage.getItem('vendorResources') || '[]')
        const filtered = existing.filter(r => r.id !== resourceId)
        sessionStorage.setItem('vendorResources', JSON.stringify(filtered))
      } catch (e) {
        console.warn('Failed to update bench resource cache after deletion:', e)
      }

      return data
    } catch (error) {
      console.error('deleteBenchResource error:', error)
      throw new Error(error.message || 'Failed to delete bench resource')
    }
  },

  async getVendorProfile(userId) {
    try {
      const token = getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await axios.get(
        `${API_BASE_URL}/vendor/profile/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch vendor profile')
      }

      // Store vendor profile data in sessionStorage
      if (response.data.data) {
        const profileData = response.data.data;

        // Store the complete vendor profile
        sessionStorage.setItem('vendorProfile', JSON.stringify(profileData));

        // Store specific profile fields if available
        if (profileData.vendorId) {
          sessionStorage.setItem('vendorId', JSON.stringify(profileData.vendorId));
        }
        if (profileData.status) {
          sessionStorage.setItem('vendorStatus', JSON.stringify(profileData.status));
        }
        if (profileData.verificationStatuses) {
          sessionStorage.setItem('verificationStatuses', JSON.stringify(profileData.verificationStatuses));
        }
      }

      return response.data
    } catch (error) {
      console.error('Get vendor profile error:', error)
      throw error
    }
  },

  async getVendorProfileByVendorId(vendorId) {
    try {
      const token = getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      // Get userId from current user - API requires userId, not vendorId
      const user = await getCurrentUser()
      const userId = user?.id || user?.user_id

      if (!userId) {
        throw new Error('User ID not found. Please login again.')
      }

      const response = await axios.get(
        `${API_BASE_URL}/vendor/profile/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
        }
      )

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch vendor profile')
      }

      return {
        success: true,
        data: response.data.data
      }
    } catch (error) {
      console.error('Error fetching vendor profile by vendorId:', error)
      throw error
    }
  },




  // Fetch specific bench resource by ID
  async fetchBenchResourceById(resourceId) {
    try {
      const authToken = getAuthToken()
      if (!authToken) {
        throw new Error('No authentication token found')
      }

      const response = await axios.get(`${API_BASE_URL}/bench-resource/${resourceId}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      })

      return response.data
    } catch (error) {
      console.error('Error fetching bench resource by ID:', error)
      throw error
    }
  },

  // Update BGV status for current user using client-side API
  async updateUserBGVStatus(bgvStatus, rejectionReason = null) {
    try {
      // Only allow "uploaded" status for client-side updates
      if (bgvStatus !== 'uploaded') {
        throw new Error('Invalid BGV status. Only "uploaded" status is allowed for client-side updates.')
      }

      const user = await getCurrentUser()
      const userId = user?.id || user?.user_id
      const token = getAuthToken()

      if (!userId) {
        throw new Error('User not authenticated')
      }

      if (!token) {
        throw new Error('Authentication required')
      }

      const requestData = {
        bgvStatus: bgvStatus
      }

      // Add rejection reason if provided
      if (rejectionReason) {
        requestData.reason = rejectionReason
      }

      const response = await axios.patch(
        `${API_BASE_URL}/bgv/${userId}`,
        requestData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to update BGV status')
      }

      return response.data
    } catch (error) {
      console.error('User BGV status update error:', error)
      throw error
    }
  },

  async getEnum(enumType) {
    try {
      const response = await axios.get(`${API_BASE_URL}/?enum=${enumType}`)

      if (response.data && response.data.items) {
        return {
          success: true,
          data: response.data.items
        }
      } else {
        throw new Error('Invalid response format')
      }
    } catch (error) {
      console.error(`Error fetching ${enumType}:`, error)
      throw error
    }
  },

  // Legacy functions for backward compatibility
  async getCountryCodes() {
    return this.getEnum('country_codes')
  },

  async getIndustryDomains() {
    return this.getEnum('industries')
  },

  async getYearsOfOperation() {
    return this.getEnum('years_in_operation')
  },

  async getTechStacks() {
    return this.getEnum('technologies')
  },


  // Update Document Status (Admin only)
  async updateDocumentStatus(documentId, status, reason) {
    try {
      const token = getAuthToken()
      if (!token) {
        throw new Error('User not authenticated. Please login again.')
      }

      console.log(`Updating document ${documentId} status to ${status} with reason: ${reason}`)

      const response = await axios.patch(`${API_BASE_URL}/admin/doc-status/${documentId}`, {
        status,
        reason
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.data.success) {
        return response.data
      } else {
        throw new Error(response.data.message || 'Failed to update document status')
      }
    } catch (error) {
      console.error('Error updating document status:', error)
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message)
      } else if (error.response?.data?.error) {
        throw new Error(error.response.data.error)
      } else {
        throw new Error(error.message || 'Failed to update document status')
      }
    }
  },

  // Get vendor registration data by vendorId
  async getVendorRegistrationData(vendorId) {
    try {
      const user = await getCurrentUser()
      const userId = user?.id || user?.user_id
      const token = getAuthToken()

      if (!userId) {
        throw new Error('User not authenticated. Please login again.')
      }

      if (!token) {
        throw new Error('Authentication token not found. Please login again.')
      }

      // Use the vendor profile endpoint which returns registration data
      const response = await axios.get(`${API_BASE_URL}/vendor/profile/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.data.success && response.data.data) {
        return {
          success: true,
          data: response.data.data
        }
      }

      throw new Error(response.data.message || 'Failed to fetch vendor registration data')
    } catch (error) {
      console.error('Error fetching vendor registration data:', error)
      throw error
    }
  },

  // Get vendor register data (Step 1 - Legal Entity Details)
  async getVendorRegisterData(vendorId, forceRefresh = false) {
    try {
      const token = getAuthToken()
      if (!token) {
        console.warn('No auth token found for getVendorRegisterData')
        // Try to return cached data
        return this.getCachedVendorRegisterData(vendorId)
      }

      if (!vendorId) {
        console.warn('No vendorId provided for getVendorRegisterData')
        return null
      }

      // Check cache first only if not forcing refresh (5 minute expiry)
      if (!forceRefresh) {
        const cached = this.getCachedVendorRegisterData(vendorId)
        if (cached) {
          console.log('Using cached vendor register data for vendorId:', vendorId)
          return cached
        }
      }

      console.log('Fetching vendor register data from API for vendorId:', vendorId, forceRefresh ? '(force refresh)' : '')
      const apiUrl = `${API_BASE_URL}/vendor-register/${vendorId}`
      console.log('API URL:', apiUrl)

      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      console.log('Vendor register API response:', response.data)

      if (response.data.success && response.data.data) {
        // Cache the response
        const cacheKey = `vendor_register_${vendorId}`
        sessionStorage.setItem(cacheKey, JSON.stringify({
          data: response.data.data,
          timestamp: Date.now()
        }))
        return {
          success: true,
          data: response.data.data
        }
      }

      return null
    } catch (error) {
      console.error('Error fetching vendor register data:', error)
      console.error('Error details:', error.response?.data || error.message)
      // Try to return cached data on failure
      return this.getCachedVendorRegisterData(vendorId)
    }
  },

  // Get cached vendor register data
  getCachedVendorRegisterData(vendorId) {
    try {
      const cacheKey = `vendor_register_${vendorId}`
      const cached = sessionStorage.getItem(cacheKey)
      if (!cached) return null

      const parsed = JSON.parse(cached)
      const fiveMinutes = 5 * 60 * 1000
      if (Date.now() - parsed.timestamp > fiveMinutes) {
        sessionStorage.removeItem(cacheKey)
        return null
      }

      return {
        success: true,
        data: parsed.data,
        cached: true
      }
    } catch (error) {
      console.error('Error reading cached vendor register data:', error)
      return null
    }
  },

  // Get vendor profile data (Step 2 - Profile Building)
  async getVendorProfileData(vendorId, forceRefresh = false) {
    try {
      const token = getAuthToken()
      if (!token) {
        console.warn('No auth token found for getVendorProfileData')
        // Try to return cached data
        return this.getCachedVendorProfileData(vendorId)
      }

      if (!vendorId) {
        console.warn('No vendorId provided for getVendorProfileData')
        return null
      }

      // Check cache first only if not forcing refresh (5 minute expiry)
      if (!forceRefresh) {
        const cached = this.getCachedVendorProfileData(vendorId)
        if (cached) {
          console.log('Using cached vendor profile data for vendorId:', vendorId)
          return cached
        }
      }

      console.log('Fetching vendor profile data from API for vendorId:', vendorId, forceRefresh ? '(force refresh)' : '')
      const apiUrl = `${API_BASE_URL}/vendor-profile/${vendorId}`
      console.log('API URL:', apiUrl)

      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      console.log('Vendor profile API response:', response.data)

      if (response.data.success && response.data.data) {
        // Cache the response
        const cacheKey = `vendor_profile_${vendorId}`
        sessionStorage.setItem(cacheKey, JSON.stringify({
          data: response.data.data,
          timestamp: Date.now()
        }))
        return {
          success: true,
          data: response.data.data
        }
      }

      return null
    } catch (error) {
      console.error('Error fetching vendor profile data:', error)
      console.error('Error details:', error.response?.data || error.message)
      // Try to return cached data on failure
      return this.getCachedVendorProfileData(vendorId)
    }
  },

  // Get cached vendor profile data
  getCachedVendorProfileData(vendorId) {
    try {
      const cacheKey = `vendor_profile_${vendorId}`
      const cached = sessionStorage.getItem(cacheKey)
      if (!cached) return null

      const parsed = JSON.parse(cached)
      const fiveMinutes = 5 * 60 * 1000
      if (Date.now() - parsed.timestamp > fiveMinutes) {
        sessionStorage.removeItem(cacheKey)
        return null
      }

      return {
        success: true,
        data: parsed.data,
        cached: true
      }
    } catch (error) {
      console.error('Error reading cached vendor profile data:', error)
      return null
    }
  },

}
export { vendorService }